import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const WHATSAPP_URL = 'https://wa.me/5511913278081?text=Ol%C3%A1%2C%20preciso%20de%20atendimento%20jur%C3%ADdico%20urgente.';

const atuacaoSublinks = [
  { label: 'Criminal', href: '/atuacao/criminal' },
  { label: 'Penal Empresarial', href: '/atuacao/penal-empresarial' },
  { label: 'Civil', href: '/atuacao/civil' },
];

export default function Header() {
  const location = useLocation();
  const isInnerPage = location.pathname !== '/';
  const [scrolled, setScrolled] = useState(isInnerPage);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileAtuacaoOpen, setMobileAtuacaoOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isInnerPage) {
      setScrolled(true);
      return;
    }
    const handleScroll = () => setScrolled(window.scrollY > 60);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInnerPage]);

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
    setMobileAtuacaoOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    setDropdownOpen(false);
    navigate(href);
  };

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const isAtuacaoActive = () => location.pathname.startsWith('/atuacao');

  const linkClass = (active: boolean) =>
    `text-sm font-sans font-medium tracking-wide transition-colors duration-300 cursor-pointer whitespace-nowrap ${
      active ? 'text-[#B39B62]' : 'text-white/80 hover:text-[#B39B62]'
    }`;

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#0A1829] shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        {/* Logo */}
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); handleNav('/'); }}
          className="flex items-center cursor-pointer"
        >
          <img
            src="https://static.readdy.ai/image/2751d92eb47f633f00285d5acf7c03f9/40cf0716b403846660cfdfa832bad018.png"
            alt="Moreira & Tofoli Advogados"
            className="h-12 w-auto"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {/* Início */}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); handleNav('/'); }}
            className={linkClass(isActive('/'))}
          >
            Início
          </a>

          {/* Área de Atuação – com dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((v) => !v)}
              className={`flex items-center gap-1.5 text-sm font-sans font-medium tracking-wide transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isAtuacaoActive() ? 'text-[#B39B62]' : 'text-white/80 hover:text-[#B39B62]'
              }`}
            >
              Área de Atuação
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {/* Dropdown */}
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-52 bg-[#0A1829] border border-white/10 rounded-lg overflow-hidden shadow-xl transition-all duration-200 ${
                dropdownOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
              }`}
            >
              {/* Arrow */}
              <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#0A1829] border-t border-l border-white/10 rotate-45"></div>

              <div className="py-2">
                {atuacaoSublinks.map((sub) => (
                  <a
                    key={sub.href}
                    href={sub.href}
                    onClick={(e) => { e.preventDefault(); handleNav(sub.href); }}
                    className={`flex items-center gap-3 px-5 py-3 text-sm font-sans transition-colors duration-200 cursor-pointer ${
                      isActive(sub.href)
                        ? 'text-[#B39B62] bg-[#B39B62]/8'
                        : 'text-white/70 hover:text-[#B39B62] hover:bg-white/5'
                    }`}
                  >
                    <span className="w-1 h-1 rounded-full bg-[#B39B62]/60 shrink-0"></span>
                    {sub.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Blog */}
          <a
            href="/blog"
            onClick={(e) => { e.preventDefault(); handleNav('/blog'); }}
            className={linkClass(isActive('/blog'))}
          >
            Blog
          </a>
        </nav>

        {/* CTA Button */}
        <div className="hidden lg:flex items-center">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#B39B62] hover:bg-[#E9D59C] text-[#0A1829] text-sm font-sans font-semibold px-5 py-3 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-base"></i>
            Atendimento imediato
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden bg-[#0A1829] border-t border-white/10 transition-all duration-300 overflow-hidden ${
          menuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); handleNav('/'); }}
            className={`text-sm font-sans font-medium tracking-wide transition-colors py-3 cursor-pointer whitespace-nowrap ${
              isActive('/') ? 'text-[#B39B62]' : 'text-white/80'
            }`}
          >
            Início
          </a>

          {/* Mobile Área de Atuação */}
          <div>
            <button
              onClick={() => setMobileAtuacaoOpen((v) => !v)}
              className={`w-full flex items-center justify-between text-sm font-sans font-medium tracking-wide transition-colors py-3 cursor-pointer whitespace-nowrap ${
                isAtuacaoActive() ? 'text-[#B39B62]' : 'text-white/80'
              }`}
            >
              Área de Atuação
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${mobileAtuacaoOpen ? 'rotate-180' : ''}`}></i>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${mobileAtuacaoOpen ? 'max-h-40' : 'max-h-0'}`}>
              <div className="pl-4 pb-2 flex flex-col gap-1">
                {atuacaoSublinks.map((sub) => (
                  <a
                    key={sub.href}
                    href={sub.href}
                    onClick={(e) => { e.preventDefault(); handleNav(sub.href); }}
                    className={`text-sm font-sans py-2 cursor-pointer flex items-center gap-2 ${
                      isActive(sub.href) ? 'text-[#B39B62]' : 'text-white/55 hover:text-[#B39B62]'
                    }`}
                  >
                    <span className="w-1 h-1 rounded-full bg-[#B39B62]/60 shrink-0"></span>
                    {sub.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <a
            href="/blog"
            onClick={(e) => { e.preventDefault(); handleNav('/blog'); }}
            className={`text-sm font-sans font-medium tracking-wide transition-colors py-3 cursor-pointer whitespace-nowrap ${
              isActive('/blog') ? 'text-[#B39B62]' : 'text-white/80'
            }`}
          >
            Blog
          </a>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-[#B39B62] text-[#0A1829] text-sm font-sans font-semibold px-5 py-3 rounded-md mt-3 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-base"></i>
            Atendimento imediato
          </a>
        </div>
      </div>
    </header>
  );
}
